import {
  createProjectSchema,
  DEFAULT_PROJECT_ICON,
  DEFAULT_PROJECT_ICON_COLOR,
  updateProjectSchema,
  type ProjectDto,
  type ProjectIcon,
  type ProjectIconColor,
} from '@tms/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button, Dialog, Field, Input, Textarea } from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { ProjectIconPicker } from './ProjectIconPicker';
import { useCreateProject, useUpdateProject } from './api';

interface CreateValues {
  name: string;
  code: string;
  description?: string | null;
  icon: ProjectIcon;
  iconColor: ProjectIconColor;
}

export interface ProjectFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present when editing; absent when creating. */
  project?: ProjectDto;
  onCreated?: (project: ProjectDto) => void;
}

export const ProjectFormDialog = ({
  open,
  onOpenChange,
  project,
  onCreated,
}: ProjectFormDialogProps) => {
  const { t } = useT();
  const { errorText, applyApiError } = useErrorText();
  const isEdit = project !== undefined;
  const create = useCreateProject();
  const update = useUpdateProject(project?.code ?? '');

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateValues>({
    resolver: zodResolver(isEdit ? updateProjectSchema : createProjectSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      icon: DEFAULT_PROJECT_ICON,
      iconColor: DEFAULT_PROJECT_ICON_COLOR,
    },
  });

  useEffect(() => {
    if (!open) return;
    reset({
      name: project?.name ?? '',
      code: project?.code ?? '',
      description: project?.description ?? '',
      icon: project?.icon ?? DEFAULT_PROJECT_ICON,
      iconColor: project?.iconColor ?? DEFAULT_PROJECT_ICON_COLOR,
    });
  }, [open, project, reset]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit) {
        await update.mutateAsync({
          name: values.name,
          description: values.description ?? null,
          icon: values.icon,
          iconColor: values.iconColor,
        });
        toast.success(t('projects.updated'));
      } else {
        const created = await create.mutateAsync({
          name: values.name,
          code: values.code,
          description: values.description ?? null,
          icon: values.icon,
          iconColor: values.iconColor,
        });
        toast.success(t('projects.created', { code: created.code }));
        onCreated?.(created);
      }
      onOpenChange(false);
    } catch (error) {
      if (!applyApiError(error, setError, ['name', 'code', 'description'])) {
        toast.error(errorText(error, 'projects.saveFailed'));
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('projects.edit') : t('projects.new')}
      description={isEdit ? undefined : t('projects.formHint')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={isSubmitting}>
            {isEdit ? t('common.saveChanges') : t('projects.create')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <Field label={t('common.name')} error={errors.name?.message} required>
          {({ id, invalid, describedBy }) => (
            <Input id={id} invalid={invalid} aria-describedby={describedBy} autoFocus {...register('name')} />
          )}
        </Field>

        {isEdit ? null : (
          <Field
            label={t('projects.fieldCode')}
            error={errors.code?.message}
            hint={t('projects.fieldCodeHint')}
            required
          >
            {({ id, invalid, describedBy }) => (
              <Input
                id={id}
                invalid={invalid}
                aria-describedby={describedBy}
                placeholder="PAY"
                className="font-mono uppercase"
                maxLength={10}
                {...register('code')}
              />
            )}
          </Field>
        )}

        <Field label={t('projects.icon')}>
          {({ id }) => (
            <Controller
              control={control}
              name="icon"
              render={({ field }) => (
                <Controller
                  control={control}
                  name="iconColor"
                  render={({ field: colorField }) => (
                    <ProjectIconPicker
                      id={id}
                      icon={field.value}
                      color={colorField.value}
                      onChange={(next) => {
                        field.onChange(next.icon);
                        colorField.onChange(next.color);
                      }}
                    />
                  )}
                />
              )}
            />
          )}
        </Field>

        <Field label={t('common.description')} error={errors.description?.message}>
          {({ id, invalid, describedBy }) => (
            <Textarea id={id} invalid={invalid} aria-describedby={describedBy} {...register('description')} />
          )}
        </Field>

        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
